import React, { useEffect, useState } from "react";
import { withRouter } from "react-router-dom";
import { connect } from "react-redux";
import { mapProperties } from "colbi_web_ui/lib/utils";
import { parse } from "query-string";
import {
    useControllerInitialization,
    organizationAndProjectSelection,
    buildGenericMenu,
    controllerQueries,
    GlobalPageLayout,
    buildNavProps,
    generateNavigation,
    getDownloadableQueries,
    headerTitle,
    buildHeaderComponents,
} from "../utils/utils";
import { applyThemeWithPriority, buildStaticLinks } from "../../utils/utils";
import { cleanPropertiesNestedInMenu } from "../../utils/mapPropertiesForMenu";
import useUpdateThemes from "../../hooks/useUpdateThemes";
import proxy from "../../routes/proxy";
import { matchRoutes } from "react-router-config";
import { asideRepositoryNavigationByModule } from "../Repository/navigation";
import I18n from "../../I18n";
import ImportProfilesEditModeButtons from "./ImportProfilesEditModeButtons";
import { SovosSnackbar } from "mosaic-react";
import {
    logout,
    pushModal,
    pushDialog,
    popModal,
    toggleMenu,
    setLocale,
    removeModal,
    updateImportProfileAction,
} from "../../store/actions";

const ImportProfiles = (props) => {
    const {
        modules,
        route,
        location,
        reload,
        matchParams,
        projectId,
        locale,
        fetchResults,
        fetchStatus,
        i18n,
        userInformation,
        history,
        pushModal,
    } = useControllerInitialization(props);

    // Get updateImportProfileAction directly from props since it's not in useControllerInitialization
    const { updateImportProfileAction } = props;

    let { fiscalYear, importId } = matchParams;

    const {
        menuOrganizationProject,
        organizationProjects,
        project,
        fiscalYearsWithImportIds,
        importIdsFromUrlFiscalYear,
        fiscalYear: finalFiscalYear,
        importId: finalImportId,
        queryStatus: importProfilesQueryStatus,
    } = organizationAndProjectSelection({
        fetchResults,
        fetchStatus,
        component: ImportProfiles,
        matchParams,
        props,
        i18n,
        locale,
        fiscalYear,
        importId,
        projectId,
    });

    const { loading = true, success = false } = importProfilesQueryStatus || {};
    const { updateThemes, LoadingComponent } = useUpdateThemes(loading, success);

    const defaultNotification = { open: false, message: "", type: "", onClose: () => {} };
    const [notification, pushNotification] = useState({ ...defaultNotification });

    // Apply theme with priority logic: Project > Organization > Default
    useEffect(() => {
        applyThemeWithPriority(organizationProjects, projectId, updateThemes, "ImportProfiles");
    }, [organizationProjects, projectId, updateThemes]);

    fiscalYear = finalFiscalYear ?? fiscalYear;
    importId = finalImportId ?? importId;

    const { navData, params, routeQueries } = buildGenericMenu(
        modules,
        fetchResults,
        locale,
        fiscalYear,
        projectId,
        importId,
        project,
        location,
        matchParams,
        route,
    );

    const title = headerTitle(
        route,
        fetchResults,
        fetchStatus,
        routeQueries,
        props,
        params,
        i18n,
        locale,
        fiscalYear,
        importId,
    );

    const headerComponents = buildHeaderComponents(
        route,
        fetchResults,
        fetchStatus,
        routeQueries,
        props,
        params,
        i18n,
        locale,
    );

    const downloadableQueries = getDownloadableQueries(matchRoutes, route, location, params);

    const { navigation } = generateNavigation({
        modules,
        asideRepositoryNavigationByModule,
        route,
        fetchResults,
        fetchStatus,
        component: ImportProfiles,
        params,
        fiscalYear,
        importId,
        props,
        navData,
        i18n,
        locale,
    });

    const { navProps, staticLinks } = buildNavProps(
        buildStaticLinks,
        navigation,
        history,
        matchParams,
        fiscalYear,
        importId,
        i18n,
        menuOrganizationProject,
        projectId,
        cleanPropertiesNestedInMenu,
        locale,
        proxy,
        params,
        userInformation,
    );

    const globalLayoutProps = {
        ...props,
        navProps,
        downloadableQueries,
        params: { ...params, fiscalYear, importId },
        staticLinks,
    };

    const [editMode, setEditMode] = useState(false);
    const [listResults, setListResults] = useState([]);
    let rowsEdited = {};
    let rawDataEdit = false;

    const params2 = {
        ...(parse(location.search) || {}),
        ...(matchParams || {}),
    };

    const saveChanges = async () => {
        if (!Object.keys(rowsEdited).length) {
            pushNotification({
                open: true,
                message: i18n`No changes to save`,
                type: "info",
                timeout: "short",
            });
            return;
        }

        const result = await updateImportProfileAction(projectId, JSON.stringify(rowsEdited));

        if (result?.update_import_profile) {
            pushNotification({
                open: true,
                message: i18n`Import profiles updated successfully`,
                type: "confirmation",
                timeout: "short",
            });
            rowsEdited = {};
            setEditMode(false);
            reload();
        } else {
            pushNotification({
                open: true,
                message: i18n`Error updating import profiles`,
                type: "error",
                timeout: "short",
            });
        }
    };

    const cancelChanges = () => {
        rowsEdited = {};
        setEditMode(false);
        reload();
    };

    const onRowEditDatagrid = (updatedRowObject, originalRowObject) => {
        const { uniqueId: updatedUniqueID, ...updatedRow } = updatedRowObject;
        const { uniqueId: originalUniqueID, ...originalRow } = originalRowObject;

        for (const key in updatedRow) {
            if (updatedRow[key] !== originalRow[key]) {
                const updateObject = {
                    [updatedRow.id]: {
                        updated: {
                            [`${key}`]: updatedRow[key],
                            ...rowsEdited[updatedRow.id]?.updated,
                        },
                        original: rowsEdited[updatedRow.id]?.original
                            ? rowsEdited[updatedRow.id]?.original
                            : originalRow,
                    },
                };
                rowsEdited = { ...rowsEdited, ...updateObject };
            }
        }
        return { ...updatedRow, uniqueId: updatedUniqueID };
    };

    const routeQueries2 = (route.queries && route.queries(params2)) || [];

    // eslint-disable-next-line no-unused-vars
    const children = route.components.map((c, index) => {
        const Component = c.component;
        const componentParsedProps = mapProperties(
            c.props,
            fetchResults,
            fetchStatus,
            routeQueries2,
            props,
            params2,
            i18n,
            locale,
        );

        const componentProps = {
            ...componentParsedProps,
            modules,
            editMode: editMode,
            onSave: saveChanges,
            onCancel: cancelChanges,
        };

        if (componentProps && componentProps.items?.length && componentProps.onRowEdit) {
            componentProps.onProcessRowUpdateErrorAction = () => {
                pushNotification({
                    variant: "error",
                    message: I18n`Error updating row`,
                });
            };
            componentProps.onRowEdit = (updatedRow, originalRow) =>
                onRowEditDatagrid(updatedRow, originalRow);
        }

        if (componentProps.items && componentProps.items.length !== listResults.length) {
            setListResults(componentProps.items || []);
        }

        rawDataEdit = componentParsedProps.rawDataEdit || false;

        return <Component key={index} {...componentProps} {...c.componentProps} />;
    });

    if (loading) {
        return LoadingComponent;
    }

    const editModeButtons = (
        <ImportProfilesEditModeButtons
            key="edit-mode-buttons"
            editMode={editMode}
            setEditMode={setEditMode}
            onClickSave={saveChanges}
            onClickCancel={cancelChanges}
            list={listResults}
            rawDataEdit={rawDataEdit}
            i18n={i18n}
        />
    );

    const titleBarActionsArray = [
        ...(headerComponents.length > 0
            ? headerComponents.map((headerComponent, index) => (
                  <headerComponent.Component
                      key={`titlebar-action-${index}`}
                      pushModal={pushModal}
                      i18n={i18n}
                      reload={reload}
                      projectId={projectId}
                      user={userInformation}
                      {...headerComponent.props}
                  />
              ))
            : []),
        editModeButtons,
    ];

    return (
        <>
            <SovosSnackbar
                onClose={() => {
                    pushNotification({
                        ...notification,
                        open: false,
                    });
                }}
                {...notification}
            />
            <GlobalPageLayout
                globalLayoutProps={globalLayoutProps}
                modules={modules}
                headerProps={{
                    fiscalYears: fiscalYearsWithImportIds,
                    importIds: importIdsFromUrlFiscalYear,
                    downloadableQueries,
                    title: title ? i18n`${title}` : i18n`Import Profiles`,
                    headerComponents: headerComponents,
                    titleBarActions: titleBarActionsArray,
                }}
                headerComponentsMapper={() => (headerComponentProperties) => (
                    <headerComponentProperties.Component
                        key="import-profiles"
                        pushModal={pushModal}
                        i18n={i18n}
                        reload={reload}
                        projectId={projectId}
                        user={userInformation}
                        {...headerComponentProperties.props}
                    />
                )}
                fetch={props.fetch}
                route={route}
                matchParams={matchParams}
                menu={props.menu}
                isLoadingContent={() => false}
            >
                {children}
            </GlobalPageLayout>
        </>
    );
};

ImportProfiles.queries = (args, userModulesByProject) => {
    return controllerQueries(args, userModulesByProject);
};

export default connect(
    ({ user, fetch, menu }) => ({
        user,
        fetch,
        menu,
    }),
    {
        logout,
        pushModal,
        popModal,
        removeModal,
        toggleMenu,
        setLocale,
        pushDialog,
        updateImportProfileAction,
    },
)(withRouter(ImportProfiles));
